import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  brokers,
  documents,
  type BrokerChannelType,
  type BrokerChannelTypeInput,
  type BrokerRequestDocumentItem,
  type BrokerRequestDocumentItemInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

import { PERMISSAO_CONFIGURAR, PERMISSAO_VER } from './rotulos';

/**
 * "Canais do corretor" em Configuracoes (Broker/BuscarTipoCanal do OnPoint).
 *
 * O canal que pede os codigos da seguradora os exige no cadastro do corretor, como o
 * OnPoint faz com o canal da seguradora parceira. O canal nao e excluido, so
 * desativado: o corretor que ja o tem continua dizendo de que canal e.
 */
export function BrokerChannelTypesPanel() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeVer = can(PERMISSAO_VER);
  const podeConfigurar = can(PERMISSAO_CONFIGURAR);
  const [editando, setEditando] = useState<BrokerChannelType | 'novo' | null>(null);

  const consulta = useQuery({
    queryKey: ['broker-channel-types'],
    queryFn: ({ signal }) => brokers.channelTypes({}, signal),
    enabled: podeVer,
  });

  const alternar = useMutation({
    mutationFn: (canal: BrokerChannelType) =>
      brokers.updateChannelType(canal.id, { active: !canal.active }),
    onSuccess: (canal) => {
      toast.success(canal.active ? 'Canal reativado.' : 'Canal desativado.');
      void queryClient.invalidateQueries({ queryKey: ['broker-channel-types'] });
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  if (!podeVer) {
    return (
      <Alert tone="info" title="Indisponivel para o seu acesso">
        Os canais do corretor exigem a leitura de corretores.
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Canais do corretor"
        description="Canais de venda das corretoras. O canal que pede os codigos da seguradora os exige no cadastro do corretor."
        action={
          podeConfigurar ? (
            <Button size="sm" onClick={() => setEditando('novo')}>
              Novo canal
            </Button>
          ) : undefined
        }
      />
      <CardBody>
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar os canais">
            {consulta.error.message}
          </Alert>
        ) : null}
        <DataTable
          loading={consulta.isPending}
          columns={[
            {
              key: 'nome',
              header: 'Canal',
              render: (canal: BrokerChannelType) => (
                <div className="min-w-0">
                  <p className="font-medium text-ink-900">{canal.name}</p>
                  {canal.description ? (
                    <p className="text-2xs text-ink-500">{canal.description}</p>
                  ) : null}
                </div>
              ),
            },
            {
              key: 'marcas',
              header: 'Configuracao',
              render: (canal: BrokerChannelType) => (
                <div className="flex flex-wrap gap-1">
                  {canal.requires_insurer_codes ? (
                    <Badge tone="info">Pede os codigos da seguradora</Badge>
                  ) : null}
                  {canal.registration_only ? <Badge>So no cadastro</Badge> : null}
                  {canal.active ? null : <Badge tone="neutral">Inativo</Badge>}
                </div>
              ),
            },
            ...(podeConfigurar
              ? [
                  {
                    key: 'acoes',
                    header: 'Acoes',
                    render: (canal: BrokerChannelType) => (
                      <div className="flex gap-1">
                        <AcaoComIcone
                          acao="editar"
                          rotulo="Editar"
                          onClick={() => setEditando(canal)}
                        />
                        <AcaoComIcone
                          acao={canal.active ? 'desativar' : 'reativar'}
                          rotulo={canal.active ? 'Desativar' : 'Reativar'}
                          loading={alternar.isPending}
                          onClick={() => alternar.mutate(canal)}
                        />
                      </div>
                    ),
                  },
                ]
              : []),
          ]}
          items={consulta.data ?? []}
          keyOf={(canal) => canal.id}
          empty={<EmptyState title="Nenhum canal cadastrado" />}
        />
      </CardBody>
      {editando ? (
        <ChannelDialog
          canal={editando === 'novo' ? undefined : editando}
          onClose={() => setEditando(null)}
          onSalvo={() => {
            setEditando(null);
            void queryClient.invalidateQueries({ queryKey: ['broker-channel-types'] });
          }}
        />
      ) : null}
    </Card>
  );
}

function ChannelDialog({
  canal,
  onClose,
  onSalvo,
}: {
  canal: BrokerChannelType | undefined;
  onClose: () => void;
  onSalvo: () => void;
}) {
  const toast = useToast();
  const [nome, setNome] = useState(canal?.name ?? '');
  const [descricao, setDescricao] = useState(canal?.description ?? '');
  const [pedeCodigos, setPedeCodigos] = useState(
    canal?.requires_insurer_codes ?? false,
  );
  const [soCadastro, setSoCadastro] = useState(canal?.registration_only ?? false);
  const [ordem, setOrdem] = useState(String(canal?.position ?? 0));

  const salvar = useMutation({
    mutationFn: (entrada: BrokerChannelTypeInput) =>
      canal
        ? brokers.updateChannelType(canal.id, entrada)
        : brokers.createChannelType(entrada),
    onSuccess: () => {
      toast.success(canal ? 'Canal alterado.' : 'Canal cadastrado.');
      onSalvo();
    },
  });
  const erro = salvar.error instanceof ApiError ? salvar.error : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate({
      name: nome,
      description: descricao,
      requires_insurer_codes: pedeCodigos,
      registration_only: soCadastro,
      position: Number.parseInt(ordem, 10) || 0,
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={canal ? `Alterar ${canal.name}` : 'Novo canal'}
      size="md"
    >
      <form onSubmit={enviar} className="flex flex-col gap-4">
        {erro ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {erro.message}
          </Alert>
        ) : null}
        <Field
          label="Nome do canal"
          required
          maxLength={120}
          value={nome}
          error={fieldErrorOf(erro, 'name')}
          onChange={(evento) => setNome(evento.target.value)}
        />
        <Textarea
          label="Descricao do canal"
          maxLength={500}
          value={descricao}
          onChange={(evento) => setDescricao(evento.target.value)}
        />
        <Field
          label="Ordem"
          inputMode="numeric"
          value={ordem}
          error={fieldErrorOf(erro, 'position')}
          onChange={(evento) => setOrdem(evento.target.value)}
        />
        <label className="flex items-center gap-2 text-xs text-ink-700">
          <input
            type="checkbox"
            checked={pedeCodigos}
            onChange={(evento) => setPedeCodigos(evento.target.checked)}
          />
          Pede o codigo do corretor, da sucursal e do contrato na seguradora
        </label>
        <label className="flex items-center gap-2 text-xs text-ink-700">
          <input
            type="checkbox"
            checked={soCadastro}
            onChange={(evento) => setSoCadastro(evento.target.checked)}
          />
          Usado no cadastro de corretor
        </label>
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={salvar.isPending}>
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * "Documentos pedidos ao corretor" em Configuracoes: o que a seguradora costuma
 * pedir as corretoras, com o tipo do catalogo de documentos e o arquivo modelo.
 *
 * O catalogo nao exige nada sozinho. Quem cuida do cadastro do corretor escolhe, na
 * aba Documentos dele, quais itens pedir. Quais sao obrigatorios para operar e regra
 * pendente (COR-002).
 */
export function BrokerRequestDocumentsPanel() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeVer = can(PERMISSAO_VER);
  const podeConfigurar = can(PERMISSAO_CONFIGURAR);
  const [editando, setEditando] = useState<BrokerRequestDocumentItem | 'novo' | null>(
    null,
  );

  const consulta = useQuery({
    queryKey: ['broker-request-documents'],
    queryFn: ({ signal }) => brokers.requestDocumentItems({}, signal),
    enabled: podeVer,
  });

  const alternar = useMutation({
    mutationFn: (item: BrokerRequestDocumentItem) =>
      brokers.updateRequestDocumentItem(item.id, { active: !item.active }),
    onSuccess: (item) => {
      toast.success(item.active ? 'Documento reativado.' : 'Documento desativado.');
      void queryClient.invalidateQueries({ queryKey: ['broker-request-documents'] });
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  if (!podeVer) {
    return (
      <Alert tone="info" title="Indisponivel para o seu acesso">
        O catalogo de documentos pedidos ao corretor exige a leitura de corretores.
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Documentos pedidos ao corretor"
        description="O que a seguradora costuma pedir as corretoras. O catalogo nao exige nada sozinho: o pedido e feito no cadastro de cada corretor."
        action={
          podeConfigurar ? (
            <Button size="sm" onClick={() => setEditando('novo')}>
              Novo documento
            </Button>
          ) : undefined
        }
      />
      <CardBody>
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar o catalogo">
            {consulta.error.message}
          </Alert>
        ) : null}
        <DataTable
          loading={consulta.isPending}
          columns={[
            {
              key: 'nome',
              header: 'Documento',
              render: (item: BrokerRequestDocumentItem) => (
                <div className="min-w-0">
                  <p className="font-medium text-ink-900">{item.name}</p>
                  {item.description ? (
                    <p className="text-2xs text-ink-500">{item.description}</p>
                  ) : null}
                </div>
              ),
            },
            {
              key: 'marcas',
              header: 'Configuracao',
              render: (item: BrokerRequestDocumentItem) => (
                <div className="flex flex-wrap gap-1">
                  {item.required ? (
                    <Badge tone="warning">Obrigatorio</Badge>
                  ) : (
                    <Badge>Opcional</Badge>
                  )}
                  {item.template_document_id ? (
                    <Badge tone="info">Com modelo</Badge>
                  ) : null}
                  {item.active ? null : <Badge tone="neutral">Inativo</Badge>}
                </div>
              ),
            },
            ...(podeConfigurar
              ? [
                  {
                    key: 'acoes',
                    header: 'Acoes',
                    render: (item: BrokerRequestDocumentItem) => (
                      <div className="flex gap-1">
                        <AcaoComIcone
                          acao="editar"
                          rotulo="Editar"
                          onClick={() => setEditando(item)}
                        />
                        <AcaoComIcone
                          acao={item.active ? 'desativar' : 'reativar'}
                          rotulo={item.active ? 'Desativar' : 'Reativar'}
                          loading={alternar.isPending}
                          onClick={() => alternar.mutate(item)}
                        />
                      </div>
                    ),
                  },
                ]
              : []),
          ]}
          items={consulta.data ?? []}
          keyOf={(item) => item.id}
          empty={<EmptyState title="Nenhum documento no catalogo" />}
        />
      </CardBody>
      {editando ? (
        <RequestDocumentDialog
          item={editando === 'novo' ? undefined : editando}
          onClose={() => setEditando(null)}
          onSalvo={() => {
            setEditando(null);
            void queryClient.invalidateQueries({
              queryKey: ['broker-request-documents'],
            });
          }}
        />
      ) : null}
    </Card>
  );
}

function RequestDocumentDialog({
  item,
  onClose,
  onSalvo,
}: {
  item: BrokerRequestDocumentItem | undefined;
  onClose: () => void;
  onSalvo: () => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const [nome, setNome] = useState(item?.name ?? '');
  const [descricao, setDescricao] = useState(item?.description ?? '');
  const [obrigatorio, setObrigatorio] = useState(item?.required ?? true);
  const [tipo, setTipo] = useState(item?.document_type_id ?? '');
  const [modelo, setModelo] = useState(item?.template_document_id ?? '');

  // Os tipos do catalogo de documentos oferecidos ao corretor, e os arquivos da
  // seguradora que podem servir de modelo.
  const tipos = useQuery({
    queryKey: ['document-types', 'dono', 'corretor'],
    queryFn: ({ signal }) =>
      documents.types({ owner_type: 'corretor', limit: 100 }, signal),
    enabled: can('documentos.documento.view'),
    retry: false,
  });
  const modelos = useQuery({
    queryKey: ['documents', { ownerType: 'seguradora', modelos: true }],
    queryFn: ({ signal }) =>
      documents.list({ owner_type: 'seguradora', limit: 100 }, signal),
    enabled: can('documentos.seguradora.view') || can('documentos.documento.view'),
    retry: false,
  });

  const salvar = useMutation({
    mutationFn: (entrada: BrokerRequestDocumentItemInput) =>
      item
        ? brokers.updateRequestDocumentItem(item.id, entrada)
        : brokers.createRequestDocumentItem(entrada),
    onSuccess: () => {
      toast.success(item ? 'Documento alterado.' : 'Documento incluido no catalogo.');
      onSalvo();
    },
  });
  const erro = salvar.error instanceof ApiError ? salvar.error : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate({
      name: nome,
      description: descricao,
      required: obrigatorio,
      document_type_id: tipo || null,
      template_document_id: modelo || null,
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={item ? `Alterar ${item.name}` : 'Novo documento pedido ao corretor'}
      size="md"
    >
      <form onSubmit={enviar} className="flex flex-col gap-4">
        {erro ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {erro.message}
          </Alert>
        ) : null}
        <Field
          label="Nome do documento"
          required
          maxLength={200}
          value={nome}
          error={fieldErrorOf(erro, 'name')}
          onChange={(evento) => setNome(evento.target.value)}
        />
        <Textarea
          label="Orientacao para a corretora"
          maxLength={1000}
          value={descricao}
          onChange={(evento) => setDescricao(evento.target.value)}
        />
        <Select
          label="Tipo de documento"
          placeholder="Sem tipo"
          options={(tipos.data?.items ?? []).map((opcao) => ({
            value: opcao.id,
            label: opcao.name,
          }))}
          value={tipo}
          error={fieldErrorOf(erro, 'document_type_id')}
          onChange={(evento) => setTipo(evento.target.value)}
        />
        <Select
          label="Arquivo modelo"
          placeholder="Sem modelo"
          options={(modelos.data?.items ?? []).map((opcao) => ({
            value: opcao.id,
            label: opcao.title,
          }))}
          value={modelo}
          error={fieldErrorOf(erro, 'template_document_id')}
          hint="Um documento da seguradora, em Documentos."
          onChange={(evento) => setModelo(evento.target.value)}
        />
        <label className="flex items-center gap-2 text-xs text-ink-700">
          <input
            type="checkbox"
            checked={obrigatorio}
            onChange={(evento) => setObrigatorio(evento.target.checked)}
          />
          Obrigatorio
        </label>
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={salvar.isPending}>
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
